import type { Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { useListQuery, type BaseListQuery } from '../../../shared/hooks/use-list-query';
import { toListParams, type ListQuerySchema } from '../../../shared/policies/list-params';
import { ApiError } from '../../../shared/shared.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type DirectoryListState<V> =
  { status: 'loading' } | { status: 'loaded'; page: Page<V>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

function toErrorState<V>(err: unknown): DirectoryListState<V> {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

function keyOf<Q extends BaseListQuery>(query: Q): string {
  return toListParams(query).toString();
}

/** The page a reference-data directory's list query asks for (garages, suppliers), re-fetched whenever that query changes. */
export function useDirectoryList<Q extends BaseListQuery, V>(schema: ListQuerySchema<Q>, fetchPage: (query: Q) => Promise<Page<V>>) {
  const list = useListQuery(schema);
  const { query } = list;
  const [state, setState] = useState<DirectoryListState<V>>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    fetchPage(query)
      .then((page) => !cancelled && setState({ status: 'loaded', page, queryKey: keyOf(query) }))
      .catch((err) => !cancelled && setState(toErrorState(err)));

    return () => {
      cancelled = true;
    };
  }, [query, fetchPage]);

  /** Re-fetches the current page after a mutation commits. */
  async function reload(): Promise<void> {
    try {
      setState({ status: 'loaded', page: await fetchPage(query), queryKey: keyOf(query) });
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing: state.status === 'loaded' && state.queryKey !== keyOf(query), reload };
}
